import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import TabContext from '@mui/lab/TabContext';
import { Add, Analytics, Mail, ViewModule } from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import ShipmentsTable from './components/tabs/ShipmentsTable/ShipmentsTable';
import MonitorPortfolio from './components/tabs/MonitorPortfolio/MonitorPortfolio';
import {
    Shipment,
    RawSupplierStats,
    SupplierInfo,
    SupplierStat,
    ShipmentsDataTableEntry,
    ConnectivityTrendGraphSeries,
    StatusTrendGraphSeries,
    ConnectivitySummary,
    DeployedShippedSummary,
} from 'types';
import {
    getShipments,
    processSupplierConnectivityStats,
    getSupplierStats,
    getSupplierInfo,
    getDeviceIdToShipmentDataTable,
    processSupplierStatusStats,
} from 'store/services';
import { OpenModalButton } from 'components/button/OpenModalButton/OpenModalButton';
import { CreateDeviceModal } from 'components/modals/CreateDeviceModal/CreateDeviceModal';
import { MonitorPortfolioSummary } from './components/tabs/MonitorPortfolioSummary/MonitorPortfolioSummary';
import { LoadingSpinner } from 'components/data/LoadingSpinner';
import { TuTabList } from 'components/common/TuTabList/TuTabList';
import { TuTab } from 'components/common/TuTab/TuTab';
import { TuTabPanel } from 'components/common/TuTabPanel/TuTabPanel';
import styles from './Monitors.module.scss';

export interface MonitorsProps {
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
}

/** The Monitor Portfolio Page. */
export function Monitors({ viewAsSupplier }: MonitorsProps) {
    const location = useLocation();
    const { user } = useAuth();

    const [value, setValue] = useState('overview');
    const [hasQueriedSupplierStats, setHasQueriedSupplierStats] =
        useState(false);
    const [shipments, setShipments] = useState<Shipment[]>([]);
    const [deviceIdToShipmentDataTable, setDeviceIdToShipmentDataTable] =
        useState<Record<string, ShipmentsDataTableEntry>>({});
    const [supplierStats, setSupplierStats] = useState<SupplierStat>({});
    const [connectivitySummary, setConnectivitySummary] =
        useState<ConnectivitySummary>({
            connectedCount: 0,
            disconnectedCount: 0,
        });
    const [deployedShippedSummary, setDeployedShippedSummary] =
        useState<DeployedShippedSummary>({ shippedCount: 0, deployedCount: 0 });
    const [connectivityTrendGraph, setConnectivityTrendGraph] =
        useState<ConnectivityTrendGraphSeries>();
    const [statusTrendGraph, setStatusTrendGraph] =
        useState<StatusTrendGraphSeries>();

    const handleChange = (_: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
        if (!viewAsSupplier)
            window.history.replaceState(null, '', `/monitors/${newValue}`);
        else
            window.history.replaceState(
                null,
                '',
                `/supplier-details/${viewAsSupplier}/monitors/${newValue}`
            );
    };

    // Change tab.
    useEffect(() => {
        const urlParts = location.pathname.split('/');
        let currentTab = urlParts.pop() ?? '';

        const monitorTabs = ['overview', 'shipments', 'summary'];
        // If the last part of the URL does not match a valid tab/sub page, default to the portfolio tab.
        if (!monitorTabs.includes(currentTab)) {
            currentTab = 'overview';
        }

        setValue(currentTab);
    }, [location.pathname]);

    // Get supplier's shipment data.
    useEffect(() => {
        if ((user?.protected || viewAsSupplier) && shipments.length === 0) {
            const supplierId = viewAsSupplier ?? user?.protected?.supplierId;
            // Do not try to get shipments for the admin account.
            if (!supplierId || supplierId === '0') return;
            getShipments(supplierId).then((data: Shipment[]) => {
                if (!!data) setShipments(data);
            });
        }
    }, [user?.protected, viewAsSupplier, shipments.length]);

    // Process shipments.
    useEffect(() => {
        if (shipments) {
            // Get order date for devices based on shipment data.
            setDeviceIdToShipmentDataTable(
                getDeviceIdToShipmentDataTable(shipments)
            );
        }
    }, [shipments]);

    // Get and process supplier stats + set graph data.
    useEffect(() => {
        if (user?.protected && !hasQueriedSupplierStats) {
            setHasQueriedSupplierStats(true);

            const supplierId = viewAsSupplier ?? user?.protected?.supplierId;
            // Do not try to get stats for the admin account.
            if (!supplierId || supplierId === '0') return;

            // Get current connectivity stats for connectivity summary.
            getSupplierInfo(supplierId).then((info: SupplierInfo) => {
                if (info.stat) setSupplierStats(info.stat);

                const connectedCount = info.stat?.connectCount ?? 0;
                let disconnectedCount = 0;

                if (info?.stat?.deployCount) {
                    disconnectedCount =
                        info.stat.deployCount - (connectedCount ?? 0);
                }

                setConnectivitySummary({
                    connectedCount,
                    disconnectedCount,
                });

                const deployedCount = info.stat?.deployCount ?? 0;
                const shippedCount = info.stat?.status?.shipped ?? 0;

                setDeployedShippedSummary({
                    deployedCount,
                    shippedCount,
                });
            });

            // Get historical status + connectivity stats for trend graphs.
            getSupplierStats(supplierId).then((stats: RawSupplierStats) => {
                if (!!stats) {
                    processSupplierConnectivityStats(stats).then((data) => {
                        setConnectivityTrendGraph(data);
                    });

                    const isInternalUser =
                        user.protected?.supplierId?.toString() === '0';
                    processSupplierStatusStats(stats, isInternalUser).then(
                        (data) => {
                            setStatusTrendGraph(data);
                        }
                    );
                }
            });
        }
    }, [user?.protected, viewAsSupplier, hasQueriedSupplierStats]);

    return (
        <div data-testid="monitors" className={styles.container}>
            <Box display={'flex'} gap={'.5rem'}>
                {/* <OpenModalButton icon={<Add />} modal={OrderMonitorsModal}>
                    Order Monitors
                </OpenModalButton> */}
                <OpenModalButton
                    icon={<Add />}
                    modal={CreateDeviceModal}
                    modalProps={{ supplierId: viewAsSupplier }}
                >
                    Deploy
                </OpenModalButton>
            </Box>
            <Box sx={{ width: '100%', typography: 'body1' }}>
                <TabContext value={value}>
                    <Box>
                        <TuTabList
                            onChange={handleChange}
                            aria-label="Monitor Portfolio Tabs"
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            <TuTab
                                data-testid="MonitorPortfolioTab"
                                icon={<ViewModule />}
                                label="Overview"
                                value="overview"
                            />
                            <TuTab
                                icon={<Mail />}
                                data-testid="ShipmentsTab"
                                label="Shipments"
                                value="shipments"
                            />
                            <TuTab
                                data-testid="SummaryTab"
                                icon={<Analytics />}
                                label="Summary"
                                value="summary"
                            />
                        </TuTabList>
                    </Box>
                    <TuTabPanel value="overview">
                        <MonitorPortfolio
                            connectivitySummary={connectivitySummary}
                            deployedShippedSummary={deployedShippedSummary}
                            deviceIdToShipmentDataTable={
                                deviceIdToShipmentDataTable
                            }
                            supplierStats={supplierStats}
                            viewAsSupplier={viewAsSupplier}
                        ></MonitorPortfolio>
                    </TuTabPanel>
                    <TuTabPanel value="shipments">
                        <ShipmentsTable
                            shipments={shipments}
                            viewAsSupplier={viewAsSupplier}
                        ></ShipmentsTable>
                    </TuTabPanel>
                    <TuTabPanel value="summary">
                        {!!user &&
                        !!supplierStats &&
                        !!connectivitySummary &&
                        !!connectivityTrendGraph ? (
                            <MonitorPortfolioSummary
                                connectivitySummary={connectivitySummary}
                                connectivityTrendGraph={connectivityTrendGraph}
                                statusTrendGraph={statusTrendGraph}
                                supplierStats={supplierStats}
                                isInternalUser={
                                    user.protected?.supplierId?.toString() ===
                                    '0'
                                }
                            />
                        ) : (
                            <LoadingSpinner />
                        )}
                    </TuTabPanel>
                </TabContext>
            </Box>
        </div>
    );
}
